import { platformService } from './platform';
import type { User } from '@/types';
import type { AnalyticsProps } from './analytics';

/**
 * The traits every identify() call carries, built in exactly one place.
 *
 * Ripples wants identify on every authenticated load, which means more than
 * one caller: the store-driven hook fires it at launch, the profile query
 * fires it again when it brings back fresher traits (the exam goals change
 * mid-session, right after the setup flow). Two callers building their own
 * trait objects is how a property ends up present on one path and missing on
 * the other — so they share this.
 *
 * Values are strings or numbers only: Ripples stores user properties as a flat
 * string map, and anything else arrives stringified in a way nobody expects.
 */
export type AppPlatform =
    | 'web_mobile' | 'web_desktop'
    | 'telegram_ios' | 'telegram_android' | 'telegram_desktop'
    | 'ios' | 'android';

/** Detailed app platform for analytics. */
export function getAppPlatform(): AppPlatform {
    // Native apps
    if (platformService.isNativeApp()) {
        return platformService.isIOS() ? 'ios' : 'android';
    }

    // Telegram Mini App
    if (platformService.isTelegramMiniApp()) {
        const tgPlatform = window.Telegram?.WebApp?.platform;
        if (tgPlatform === 'ios') return 'telegram_ios';
        if (tgPlatform === 'android') return 'telegram_android';
        return 'telegram_desktop';
    }

    // Web - detect mobile vs desktop
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    return isMobile ? 'web_mobile' : 'web_desktop';
}

export function identityTraits(user: User): AnalyticsProps {
    return {
        name: user.name,
        email: user.email,
        avatar_url: user.telegram_photo_url,
        locale: user.locale,
        target_language: user.target_language,
        reminder_frequency: user.reminder_frequency,
        // What the user told the setup flow they are aiming for, so a segment
        // can be built on the goal itself rather than on inferred behaviour.
        greek_exam_goal: user.greek_exam_goal,
        culture_exam_goal: user.culture_exam_goal,
        // Becomes signed_up_at. If the first identify Ripples ever sees for an
        // account carries no signup date, it dates the account "now" and the
        // signups chart and every retention cohort are wrong from then on.
        created_at: user.created_at,
        is_anonymous: user.is_anonymous,
        platform: getAppPlatform(),
        plan: planOf(user),
    };
}

/**
 * One flat plan label so people, retention and revenue can be cut by
 * free vs. paying without joining on revenue events.
 *
 * `is_pro` is the Hallouminati lifetime guide and is NOT scoped per app, so
 * it must not leak into GreekBrain's plan. The GreekBrain subscription fields
 * are only present on GreekBrain payloads, which is how the two are told
 * apart. `trial` = free AI credits left; `free` = the trial is spent.
 */
function planOf(user: User): 'pro' | 'trial' | 'free' {
    if (user.is_greekbrain_subscribed !== undefined) {
        if (user.is_greekbrain_subscribed) return 'pro';
        return (user.greekbrain_trial_remaining ?? 0) > 0 ? 'trial' : 'free';
    }
    return user.is_pro ? 'pro' : 'free';
}
